import { LitElement, html, nothing, type PropertyValues } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { consume } from '@lit/context'
import { style } from 'components/shared/product-tile-configurator/components/tile-configurator-canvas.style.js'
import type { InternalProduct } from 'components/discovery/product/tile-parts/tile-parts-typing.js'
import { productTileProductContext } from 'components/discovery/product/tile-parts/tile-product-context.js'
import type { AreaPaddingCfg, FlexDirection, SimpleWidgetCfg } from 'components/shared/base-product-tile/base-product-tile-types.js'
import { buildWidgetElement } from 'components/shared/base-product-tile/widget-renderer.js'
import { isWidgetVisible } from 'components/shared/base-product-tile/widget-rules.js'
import { MAX_COLUMNS_PER_ROW, MAX_ROWS, isMobileConfigMode, layoutKindFor, type ConfigMode, type EditorRowLayout, type Section, type Widget } from 'components/shared/product-tile-configurator/editor-state.js'
import { displayConfigForMode } from 'components/shared/product-tile-configurator/image-config-mapper.js'
import { DISPLAY_NAMES, hasVisibilityConditions, visibilityBadgeLabel } from 'components/shared/product-tile-configurator/widget-metadata.js'
import { csSpacing } from '@cloudsuite/mosaic-shared/styles/spacing.style.js'
import {
  areaFor,
  AreaLayoutContext,
  AreaPaddingContext,
  CanvasStateContext,
  ImageConfigContext,
  ImageFocusContext,
  MobileModeContext,
  SelectedAreaContext,
  SelectedWidgetContext,
  TileConfiguratorActionsContext,
  type AreaLayoutContextType,
  type AreaPaddingContextType,
  type CanvasStateContextType,
  type DynamicArea,
  type ImageConfigContextType,
  type ImageFlowSlot,
  type ImageFocusContextType,
  type MobileModeContextType,
  type SelectedAreaContextType,
  type SelectedWidgetContextType,
  type TileConfiguratorActionsContextType,
} from 'components/shared/product-tile-configurator/context.js'

const LOCALE = 'en-US'

const STANDARD_IMAGE_PLACES = ['top-left', 'top-center', 'top-right', 'middle-left', 'middle-right', 'bottom-left', 'bottom-center', 'bottom-right']

const stripSectionPrefix = (id: string): string => id.replace(/^(product-info-|order-actions-)/, '')

// Mirrors base-product-tile.ts's own paddingStyleFor — an author-configured area padding
// renders as literal inline padding, same in the canvas preview as in production. Used for
// the image area and the image-flow zones (above/below), whose unconfigured default really is
// 0 (see base-product-tile.style.ts's .product-tile-header — the old theme var fallback there
// is legacy-only, never the canvas's own default). Unlike production (which routes the image
// area's own padding through cse-card's --cse-card-header-padding-y/x instead — see
// base-product-tile.ts's cardSlotPaddingVars — specifically because .product-tile-header isn't
// also a click/hover/selection target there), .canvas-image-section keeps literal padding-* on
// itself deliberately: it doubles as that target here, so its own box needs to actually
// include the padding for its outline to visually surround the whole padded area. See the
// box-sizing: content-box note on :host(.variant-wide) .canvas-image-section in
// tile-configurator-canvas.style.ts for how the equivalent fixed-width-column conflict is
// avoided here instead.
const paddingStyleFor = (cfg: AreaPaddingCfg | undefined): string => {
  if (!cfg) return ''
  return [
    ...(cfg.paddingTop ? [`padding-top:${cfg.paddingTop}`] : []),
    ...(cfg.paddingRight ? [`padding-right:${cfg.paddingRight}`] : []),
    ...(cfg.paddingBottom ? [`padding-bottom:${cfg.paddingBottom}`] : []),
    ...(cfg.paddingLeft ? [`padding-left:${cfg.paddingLeft}`] : []),
  ].join(';')
}

// productInfo/orderActions are different: left unconfigured, cse-card's own .body/.footer
// still render a real, visible spacing05 (10px) padding by default (card.style.ts). The
// canvas always takes over that padding onto its own light-DOM wrapper instead (see
// :host cse-card rules in the stylesheet, which always zero cse-card's own contribution) —
// so this wrapper's box exactly matches the visible padded area in every state, configured
// or not, and can be reliably used as the click/hover/selection target for the whole area.
const DEFAULT_AREA_PADDING = `${csSpacing.spacing05}`
const effectivePaddingStyleFor = (cfg: AreaPaddingCfg | undefined): string => [
  `padding-top:${cfg?.paddingTop || DEFAULT_AREA_PADDING}`,
  `padding-right:${cfg?.paddingRight || DEFAULT_AREA_PADDING}`,
  `padding-bottom:${cfg?.paddingBottom || DEFAULT_AREA_PADDING}`,
  `padding-left:${cfg?.paddingLeft || DEFAULT_AREA_PADDING}`,
].join(';')

@customElement('csc-tile-configurator-canvas')
export class CSCTileConfiguratorCanvas extends LitElement {
  static override styles = [style]

  @property({ type: String })
  mode!: ConfigMode

  // Set while the configurator's "preview" button is hovered — hides every editor-only border,
  // outline, placeholder and affordance (see :host([clean-preview]) in the stylesheet) so the
  // canvas shows the tile the way it really renders.
  @property({ type: Boolean, reflect: true, attribute: 'clean-preview' })
  cleanPreview = false

  @consume({ context: CanvasStateContext, subscribe: true })
  private sectionsByMode!: CanvasStateContextType

  @consume({ context: AreaLayoutContext, subscribe: true })
  private areaLayoutByMode!: AreaLayoutContextType

  @consume({ context: SelectedWidgetContext, subscribe: true })
  private selectedZone!: SelectedWidgetContextType

  @consume({ context: SelectedAreaContext, subscribe: true })
  private selectedArea!: SelectedAreaContextType

  @consume({ context: ImageFocusContext, subscribe: true })
  private imageFocused!: ImageFocusContextType

  @consume({ context: ImageConfigContext, subscribe: true })
  private imageConfig!: ImageConfigContextType

  @consume({ context: AreaPaddingContext, subscribe: true })
  private areaPaddingByMode!: AreaPaddingContextType

  @consume({ context: MobileModeContext, subscribe: true })
  private mobileMode!: MobileModeContextType

  @consume({ context: TileConfiguratorActionsContext })
  private actions!: TileConfiguratorActionsContextType

  @consume({ context: productTileProductContext, subscribe: true })
  private product!: InternalProduct

  @state()
  private dragOverSectionId: string | null = null

  @state()
  private draggingWidgetId: string | null = null

  // Origin zone of the widget currently being dragged — set alongside draggingWidgetId at drag
  // start, so the widget-level drop handlers below can tell a same-zone reorder drag apart from
  // a cross-zone move without re-parsing the drag payload.
  @state()
  private draggingSectionId: string | null = null

  // The specific widget currently being hovered as a same-zone swap target (see
  // handleWidgetDragEnterOnWidget) — distinct from dragOverSectionId, which highlights the
  // whole zone as a valid (append/move) drop target.
  @state()
  private dragOverWidgetId: string | null = null

  // Grid stays 'default' in mobile preview too (same as production's csc-plp-grid, which only
  // ever switches card-variant for list — see getEffectiveCardVariant). List switches from
  // 'wide' to 'inline', the exact same real card-variant a mobile viewport gets in production;
  // both branches render the same list section data, this is purely a visual preview.
  private get cardVariant(): 'default' | 'wide' | 'inline' {
    if (layoutKindFor(this.mode) !== 'list') return 'default'
    return this.mobileMode || isMobileConfigMode(this.mode) ? 'inline' : 'wide'
  }

  // Was firstUpdated (mode never changes after creation) — moved to updated() because
  // mobileMode can now flip cardVariant after the initial render, and the host class drives
  // the wide/inline-only CSS in the stylesheet (fixed-width image column etc.).
  protected override updated(changedProperties: PropertyValues): void {
    super.updated(changedProperties)
    this.classList.toggle('variant-wide', this.cardVariant === 'wide')
    this.classList.toggle('variant-inline', this.cardVariant === 'inline')
    this.classList.toggle('mobile-preview', this.mobileMode)
    this.shadowRoot?.querySelectorAll('.canvas-widget-instance:not(.hidden-by-rules)').forEach(instance => {
      Array.from(instance.children).forEach(child => void CSCTileConfiguratorCanvas.neutralizeNativeDrag(child))
      void CSCTileConfiguratorCanvas.markEmptyWidgetPreview(instance)
    })
    // Same fix as above, for the product image itself: csc-product-image renders a real <a
    // href="..."> in its own shadow root whenever the product has a page link, and the
    // browser's UA stylesheet sets cursor:pointer directly on that element — which still wins
    // over .canvas-image-section's own cursor:default in mobile mode (see :host(.mobile-
    // preview) in the stylesheet) since an element's own cursor always beats an inherited one.
    // cursor:inherit here makes it defer to whatever .canvas-image-section decides instead
    // (pointer normally, default in mobile preview), and this call is unconditional (not
    // gated on mobileMode) since inherit is the correct behavior either way.
    const image = this.shadowRoot?.querySelector('.canvas-image-wrapper csc-product-image')
    if (image) void CSCTileConfiguratorCanvas.neutralizeNativeDrag(image)
  }

  // Native HTML5 drag-and-drop hands drag-source ownership to the *nearest* draggable element
  // under the pointer, and an <a>/<img> is implicitly draggable in every browser with no
  // draggable="true" needed — so a widget like csc-product-tile-title or
  // csc-product-tile-variant-indication, which renders a real <a> (via cse-link) somewhere
  // inside itself, hijacks the gesture before it ever reaches this wrapper's own
  // draggable="true"/dragstart below. widget.type is arbitrary and its render tree is opaque
  // (nested custom elements, each with their own shadow root and their own async first render),
  // so rather than special-casing cse-link this walks whatever DOM a widget actually produced,
  // across every shadow boundary, and turns that native draggability off at the source. Widget
  // elements are recreated fresh on every render (buildWidgetElement in widget-renderer.ts), so
  // this has to re-run after every update, not just once.
  //
  // Same element also gets an inline cursor:inherit: cse-link's own stylesheet hardcodes
  // cursor:pointer on the <a>, and an element's own explicit cursor always wins over whatever an
  // ancestor sets (.canvas-widget-instance's cursor:grab/:active cursor:grabbing below) — inline
  // style beats any stylesheet rule regardless of shadow-root encapsulation, so this is the only
  // way to let that grab/grabbing cursor show through while hovering the link text itself. Note
  // this only fixes the hover-before-drag cursor — once a real native HTML5 drag is under way the
  // browser/OS owns the drag cursor and ignores page CSS entirely, so there's no way to keep
  // showing a custom "grabbing" glyph for the duration of an actual drag.
  private static async neutralizeNativeDrag(el: Element): Promise<void> {
    const asLitElement = el as unknown as { updateComplete?: Promise<unknown> }
    if (asLitElement.updateComplete) {
      await asLitElement.updateComplete.catch(() => undefined)
    }
    if (el instanceof HTMLAnchorElement || el instanceof HTMLImageElement) {
      el.draggable = false
      el.style.cursor = 'inherit'
    }
    const children = [...(el.shadowRoot?.children ?? []), ...el.children]
    await Promise.all(children.map(child => CSCTileConfiguratorCanvas.neutralizeNativeDrag(child)))
  }

  // Some widgets legitimately render nothing for a given product — e.g.
  // csc-product-tile-variants only shows anything when the product has variants,
  // csc-product-tile-tags only shows anything when the product has tags. That
  // is correct production behavior (base-product-tile.ts is untouched by this), but in the canvas
  // it leaves a placed widget with zero visible area: nothing to see, nothing to click, and no way
  // to tell it was ever dragged in at all. Rather than duplicating every widget's own "should I
  // render" condition here (opaque per widget type, and would drift out of sync with the real
  // logic), this checks what the widget's own render actually produced — if its shadow root has
  // no element children (Lit's `nothing` sentinel renders as marker comments only), the wrapping
  // .canvas-widget-instance gets a min-size + faded outline so it stays visible and selectable.
  // Deliberately NOT a getBoundingClientRect() check: every tile-part widget's :host is
  // `display: contents` (tile-parts.style.ts's partsStyle), and per spec a `display: contents`
  // element's own bounding rect is always all-zero regardless of what it renders — that would
  // (and, before this fix, did) flag every widget as "empty", not just genuinely empty ones.
  // Purely a canvas affordance; nothing here is ever exported. Same reason neutralizeNativeDrag
  // has to re-run after every update: buildWidgetElement recreates widget elements fresh on every
  // render, so a stale class from a previous product/preset would otherwise stick around.
  private static async markEmptyWidgetPreview(instance: Element): Promise<void> {
    const widgetElement = Array.from(instance.children).find(child => !child.classList.contains('widget-remove-affordance') && !child.classList.contains('widget-visibility-dot') && !child.classList.contains('empty-preview-label'))
    const asLitElement = widgetElement as unknown as { updateComplete?: Promise<unknown> } | undefined
    if (asLitElement?.updateComplete) {
      await asLitElement.updateComplete.catch(() => undefined)
    }
    const shadowRoot = widgetElement?.shadowRoot
    const isEmpty = !!shadowRoot && shadowRoot.childElementCount === 0
    instance.classList.toggle('empty-preview', isEmpty)
  }

  private getSections(dropAreaType: NonNullable<Section['dropAreaType']>): Section[] {
    return this.sectionsByMode[this.mode].filter(s => s.dropAreaType === dropAreaType)
  }

  private handleDragOver(event: DragEvent) {
    event.preventDefault()
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move'
    }
  }

  private handleDragEnter(event: DragEvent, sectionId: string) {
    event.preventDefault()
    this.dragOverSectionId = sectionId
  }

  private handleDragLeave(event: DragEvent, sectionId: string) {
    const currentTarget = event.currentTarget as HTMLElement
    const relatedTarget = event.relatedTarget as Node | null
    if (relatedTarget && currentTarget.contains(relatedTarget)) return
    if (this.dragOverSectionId === sectionId) {
      this.dragOverSectionId = null
    }
  }

  private handleDrop(event: DragEvent, sectionId: string) {
    event.preventDefault()
    this.dragOverSectionId = null
    const payload = event.dataTransfer?.getData('application/json')
    if (!payload) return
    try {
      const { widgetId, mode: sourceMode, type, properties } = JSON.parse(payload)
      if (widgetId) {
        // sourceMode may differ from this.mode — dragging a widget from the grid canvas onto the
        // list canvas (or vice versa) is allowed, same as moving it between two zones within one
        // canvas (see actions.moveWidget).
        this.actions.moveWidget(sourceMode, this.mode, widgetId, sectionId)
      } else {
        this.actions.placeWidget(this.mode, sectionId, type, properties)
      }
    } catch {
      // Ignore malformed drag payloads (e.g. an external, non-widget drag)
    }
  }

  private handleWidgetDragStart(event: DragEvent, widget: Widget, sectionId: string) {
    event.stopPropagation()
    if (event.dataTransfer) {
      event.dataTransfer.setData('application/json', JSON.stringify({ widgetId: widget.id, mode: this.mode, type: widget.type, properties: widget.properties }))
      event.dataTransfer.effectAllowed = 'move'
    }
    this.draggingWidgetId = widget.id
    this.draggingSectionId = sectionId
  }

  private handleWidgetDragEnd(event: DragEvent) {
    event.stopPropagation()
    this.draggingWidgetId = null
    this.draggingSectionId = null
    this.dragOverWidgetId = null
  }

  // Reordering within a zone: dropping one widget directly onto another swaps their positions
  // (see actions.swapWidgets) — a more specific drop target than the zone's own background
  // (handleDragEnter/handleDragOver/handleDragLeave/handleDrop above), which still just
  // appends/moves a widget into the zone. Only intercepts (preventDefault + stopPropagation)
  // when the drag actually originated from a widget in this same zone, via draggingSectionId —
  // a cross-zone drag or a new widget dragged from the palette (draggingWidgetId never gets set
  // for those) falls straight through, unhandled, to the zone's own listeners above.
  private handleWidgetDragEnterOnWidget(event: DragEvent, sectionId: string, widget: Widget) {
    if (!this.draggingWidgetId || this.draggingWidgetId === widget.id || this.draggingSectionId !== sectionId) return
    event.preventDefault()
    event.stopPropagation()
    this.dragOverWidgetId = widget.id
  }

  private handleWidgetDragOverOnWidget(event: DragEvent, sectionId: string) {
    if (!this.draggingWidgetId || this.draggingSectionId !== sectionId) return
    event.preventDefault()
    event.stopPropagation()
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'move'
  }

  private handleWidgetDragLeaveOnWidget(event: DragEvent, widget: Widget) {
    const currentTarget = event.currentTarget as HTMLElement
    const relatedTarget = event.relatedTarget as Node | null
    if (relatedTarget && currentTarget.contains(relatedTarget)) return
    if (this.dragOverWidgetId === widget.id) this.dragOverWidgetId = null
  }

  private handleWidgetDropOnWidget(event: DragEvent, sectionId: string, widget: Widget) {
    if (!this.draggingWidgetId || this.draggingWidgetId === widget.id || this.draggingSectionId !== sectionId) return
    event.preventDefault()
    event.stopPropagation()
    this.dragOverWidgetId = null
    this.actions.swapWidgets(this.mode, this.draggingWidgetId, widget.id)
  }

  private toWidgetCfg(w: Widget): SimpleWidgetCfg {
    return { type: w.type, properties: w.properties }
  }

  private renderWidgetInstance(widget: Widget, sectionId: string) {
    const isSelected = this.selectedZone?.mode === this.mode && this.selectedZone?.widgetId === widget.id
    const isDragging = this.draggingWidgetId === widget.id
    // Capture-phase, not bubble: a widget's own click handler (e.g.
    // csc-order-template-slide-in's trigger, which calls stopPropagation() to open the order
    // templates instead of navigating) can swallow a bubble-phase listener before it ever reaches this
    // wrapper. Capturing on the way down selects the widget — and prevents its live click
    // behavior from firing — regardless of what the widget itself does with the event. The
    // widget-remove-affordance button is exempted the same way: without this check, clicking it
    // would select the widget instead of removing it (this capturing handler would intercept and
    // stop the click before it ever reached the button's own bubble-phase listener).
    //
    // Always attached, even in mobile mode: several widgets (csc-product-tile-title,
    // csc-product-tile-variant-indication, ...) render a real <a> internally, and preventDefault
    // here is what stops it from navigating to the product page on click — that has to keep
    // happening regardless of whether selection itself is enabled right now (same reasoning as
    // .canvas-image-section's own click handler below).
    const selectHandler = {
      capture: true,
      handleEvent: (e: Event) => {
        if (e.target instanceof HTMLElement && e.target.closest('.widget-remove-affordance')) return
        e.preventDefault()
        e.stopPropagation()
        if (!this.mobileMode) this.actions.selectWidget({ mode: this.mode, sectionId, widgetId: widget.id })
      },
    }
    const isDropTarget = this.dragOverWidgetId === widget.id
    // Mobile preview is a fully inert render — no selection, no drag, no delete. Rather than
    // threading a mobileMode check into every handler above, the bindings themselves become
    // undefined (Lit removes the listener entirely) and draggable is turned off at the element,
    // which also means none of those handlers can ever fire in this mode to begin with.
    // selectHandler is the one exception — see its own comment above.
    const interactive = !this.mobileMode
    const dragStartHandler = interactive ? (e: DragEvent) => this.handleWidgetDragStart(e, widget, sectionId) : undefined
    const dragEnterHandler = interactive ? (e: DragEvent) => this.handleWidgetDragEnterOnWidget(e, sectionId, widget) : undefined
    const dragOverHandler = interactive ? (e: DragEvent) => this.handleWidgetDragOverOnWidget(e, sectionId) : undefined
    const dragLeaveHandler = interactive ? (e: DragEvent) => this.handleWidgetDragLeaveOnWidget(e, widget) : undefined
    const dropHandler = interactive ? (e: DragEvent) => this.handleWidgetDropOnWidget(e, sectionId, widget) : undefined
    const removeButton = interactive ? html`
      <button
        type="button"
        class="widget-remove-affordance"
        aria-label="Delete widget"
        @click=${(e: Event) => { e.stopPropagation(); this.actions.removeWidget(this.mode, widget.id) }}>
        <cse-icon icon-name="xmark" icon-style="light"></cse-icon>
      </button>
    ` : nothing
    const hasVisibility = hasVisibilityConditions(widget)
    // A dot normally; while the widget is selected it expands into a badge naming the rule.
    const visibilityLabel = visibilityBadgeLabel(widget) ?? 'Has visibility conditions'
    const visibilityDot = hasVisibility
      ? html`
        <span class="widget-visibility-dot" title=${visibilityLabel} aria-label=${visibilityLabel}>
          <cse-text class="widget-visibility-label" variant="caption-m">${visibilityLabel}</cse-text>
        </span>
      `
      : nothing
    // A widget whose showWhen rules don't match the preview product collapses into a placeholder
    // chip instead of rendering, so the canvas shows what this product really gets while the
    // widget stays selectable, draggable and removable (clean-preview hides the chip entirely).
    const hiddenByRules = !isWidgetVisible(widget, this.product)
    const instanceClass = `canvas-widget-instance ${isSelected ? 'selected' : ''} ${isDragging ? 'dragging' : ''} ${isDropTarget ? 'widget-drop-target' : ''} ${hasVisibility ? 'has-visibility' : ''} ${hiddenByRules ? 'hidden-by-rules' : ''}`

    const hiddenLabel = `${DISPLAY_NAMES[widget.type] ?? widget.type} · hidden for this product`
    const element = hiddenByRules
      ? html`<cse-text class="hidden-by-rules-label" variant="caption-s">${hiddenLabel}</cse-text>`
      : buildWidgetElement(this.toWidgetCfg(widget), { product: this.product, locale: LOCALE })
    if (!element) return nothing
    // Same text for a widget that renders nothing because its own built-in visibility doesn't
    // match (no levies, no variants, ...). Only shown once markEmptyWidgetPreview flags it, so the
    // widget's own render stays the single source of whether it's empty.
    const emptyLabel = hiddenByRules ? nothing : html`<cse-text class="empty-preview-label" variant="caption-s">${hiddenLabel}</cse-text>`

    return html`
      <div
        class="${instanceClass}"
        draggable="${interactive ? 'true' : 'false'}"
        @click=${selectHandler}
        @dragstart=${dragStartHandler}
        @dragenter=${dragEnterHandler}
        @dragover=${dragOverHandler}
        @dragleave=${dragLeaveHandler}
        @drop=${dropHandler}
        @dragend=${interactive ? this.handleWidgetDragEnd : undefined}>
        ${removeButton}
        ${visibilityDot}
        ${element}
        ${emptyLabel}
      </div>
    `
  }

  private renderZone(section: Section, placeClass: string) {
    const widgets = section.widgets.slice().sort((a, b) => a.sequence - b.sequence)
    const isZoneSelected = this.selectedZone?.mode === this.mode && this.selectedZone?.sectionId === section.id && !this.selectedZone?.widgetId
    const isDragOver = this.dragOverSectionId === section.id
    // image-flow (above/below) is a flex zone too, same as product-info/product-footer — see
    // ImageFlowZoneCfg — just never removable (areaFor deliberately doesn't resolve it to a
    // DynamicArea, so the remove-zone-affordance below correctly never renders for it) and
    // never split into more than this one zone.
    const isFlexZone = section.dropAreaType === 'product-info' || section.dropAreaType === 'product-footer' || section.dropAreaType === 'image-flow'
    const isImageFlowZone = section.dropAreaType === 'image-flow'
    const dynamicArea = areaFor(section.dropAreaType)
    const zoneDirection: FlexDirection = section.direction ?? 'column'
    const zoneStyle = [
      ...(isFlexZone ? [
        `flex-direction:${zoneDirection}`,
        `justify-content:${section.justifyContent ?? 'flex-start'}`,
        `align-items:${section.alignItems ?? 'stretch'}`,
        `flex-wrap:${section.wrap ? 'wrap' : 'nowrap'}`,
        ...(section.gap ? [`gap:${section.gap}`] : []),
      ] : []),
      // The zone's own box carries its padding directly (unlike productInfo/orderActions,
      // where a *separate* outer .canvas-body-area/.canvas-footer-area wrapper does — above/
      // below have no row/column layout, so the zone itself already is the whole area, no
      // separate area-level wrapper needed). Real unconfigured default is 0, same as the
      // image area's own padding — not forced to a real value like productInfo/orderActions'
      // effectivePaddingStyleFor, since production's own real default is 0 too (see
      // base-product-tile.style.ts's .image-above/.image-below).
      ...(isImageFlowZone ? [paddingStyleFor(this.areaPaddingByMode[this.mode][section.id as ImageFlowSlot])] : []),
    ].filter(Boolean).join(';')

    return html`
      <div
        class="canvas-zone ${placeClass} ${isImageFlowZone ? 'dynamic-zone' : ''} ${widgets.length === 0 ? 'empty' : ''} ${isZoneSelected ? 'zone-selected' : ''} ${isDragOver ? 'drag-over' : ''}"
        style="${zoneStyle}"
        @dragenter=${this.mobileMode ? undefined : (e: DragEvent) => this.handleDragEnter(e, section.id)}
        @dragover=${this.mobileMode ? undefined : this.handleDragOver}
        @dragleave=${this.mobileMode ? undefined : (e: DragEvent) => this.handleDragLeave(e, section.id)}
        @drop=${this.mobileMode ? undefined : (e: DragEvent) => this.handleDrop(e, section.id)}
        @click=${this.mobileMode ? undefined : (e: Event) => { e.stopPropagation(); this.actions.selectWidget({ mode: this.mode, sectionId: section.id, widgetId: null }) }}>
        ${dynamicArea && !this.mobileMode ? html`
          <button
            type="button"
            class="zone-remove-affordance"
            aria-label="Remove zone"
            @click=${(e: Event) => { e.stopPropagation(); this.actions.removeZone(this.mode, dynamicArea, section.id) }}>
            <cse-icon icon-name="xmark" icon-style="light"></cse-icon>
          </button>
        ` : nothing}
        ${widgets.length === 0
          ? html`<cse-text class="zone-placeholder" variant="body-s">
              ${isDragOver ? 'Drop here' : ''}
            </cse-text>`
          : widgets.map(w => this.renderWidgetInstance(w, section.id))}
      </div>
    `
  }

  private renderImageOverlayGrid() {
    const sections = this.getSections('image-overlay').filter(s => STANDARD_IMAGE_PLACES.includes(s.id))
    return html`
      <div class="canvas-image-overlay-grid">
        ${sections.map(s => this.renderZone(s, stripSectionPrefix(s.id)))}
      </div>
    `
  }

  private renderListImageZones(slot: 'above' | 'below') {
    const section = this.getSections('image-flow').find(s => s.id === slot)
    if (!section) return nothing
    return html`
      <div class="canvas-image-flow-zone">
        ${this.renderZone(section, stripSectionPrefix(section.id))}
      </div>
    `
  }

  private renderDynamicRow(row: EditorRowLayout, rowIndex: number, area: DynamicArea, columnGap: string | undefined) {
    const allSections = this.sectionsByMode[this.mode]
    const zoneSections = row.map(id => allSections.find(s => s.id === id)).filter((s): s is Section => !!s)
    const canAddColumn = row.length < MAX_COLUMNS_PER_ROW && !this.mobileMode
    // Column gap between the up to 2 zones in a split row — resolved to a real value
    // (configured, or the same spacing05 default the CSS itself falls back to) rather than
    // left conditional, same reasoning as renderDynamicArea's row gap: --add-column-gap below
    // always needs a real number, not "unset".
    const resolvedColumnGap = columnGap || `${csSpacing.spacing05}`
    // --add-column-gap exposed the same way --add-row-gap is (see renderDynamicArea) — lets
    // the add-column-affordance's reserved space (see .canvas-zone-row-wrapper in the
    // stylesheet) match the real gap a new column would have.
    const rowStyle = `gap:${resolvedColumnGap};--add-column-gap:${resolvedColumnGap}`

    // A stable outer wrapper (never itself resizes) hosts the hover trigger and the
    // affordance; .canvas-zone-row (inside it) is what actually shrinks on hover to make
    // room. Hovering the row directly — its own size changing as a *result* of that same
    // hover — oscillates: hover grows margin-right, the row shrinks out from under the
    // cursor, hover ends, the row grows back under the cursor, hover starts again. See
    // .canvas-zone-row-wrapper's own comment in the stylesheet.
    return html`
      <div class="canvas-zone-row-wrapper ${canAddColumn ? 'has-add-column-affordance' : ''}">
        <div class="canvas-zone-row ${row.length > 1 ? 'split' : 'full-width'}" style="${rowStyle}">
          ${zoneSections.map(section => this.renderZone(section, 'dynamic-zone'))}
        </div>
        ${canAddColumn ? html`
          <button
            type="button"
            class="add-column-affordance"
            aria-label="Add column to this row"
            @click=${(e: Event) => { e.stopPropagation(); this.actions.addColumn(this.mode, area, rowIndex) }}>
            <cse-icon icon-name="plus" icon-style="light"></cse-icon>
          </button>
        ` : nothing}
      </div>
    `
  }

  // The outer element is what carries the area's padding, click-to-select, and hover/selected
  // border — matching cse-card's own .body/.footer, the elements that actually show padding.
  // The inner element (rows + add-row affordance) is pure layout, no padding of its own.
  private renderDynamicArea(area: DynamicArea, slotName: 'body' | 'footer', innerClass: string, outerClass: string) {
    const rows = this.areaLayoutByMode[this.mode][area]
    const settings = this.areaPaddingByMode[this.mode][area]
    const isAreaSelected = this.selectedArea?.mode === this.mode && this.selectedArea?.area === area
    const canAddRow = rows.length < MAX_ROWS && !this.mobileMode
    // Row gap between stacked rows — mirrors .content's gap in base-product-tile.style.ts.
    // Resolved to a real value (configured, or the same spacing05 default the CSS itself
    // falls back to) rather than left conditional, so --add-row-gap below always has a real
    // number to work with — the add-row-affordance needs the *actual* gap, not "unset".
    const rowGap = settings?.rowGap || `${csSpacing.spacing05}`
    // Exposed as a custom property so the add-row-affordance (out-of-flow, positioned via the
    // stylesheet) can offset itself by the same gap a real new row would actually have,
    // instead of sitting flush against the last row — see .add-row-affordance's own comment.
    // List view only, same as production (renderDynamicSection). Aligned on the outer area so
    // the inner rows container — and the add-row-affordance anchored to its bottom — stays
    // content-sized; only space-between needs the inner container itself to fill the area.
    const verticalAlign = this.cardVariant !== 'default' ? settings?.verticalAlign : undefined
    const alignStyle = verticalAlign ? `justify-content:${verticalAlign}` : ''
    const innerStyle = [`gap:${rowGap};--add-row-gap:${rowGap}`, alignStyle, verticalAlign === 'space-between' ? 'flex-grow:1' : ''].filter(Boolean).join(';')
    return html`
      <div
        slot="${slotName}"
        class="${outerClass} ${isAreaSelected ? 'area-selected' : ''}"
        style="${[effectivePaddingStyleFor(settings), alignStyle].filter(Boolean).join(';')}"
        @click=${this.mobileMode ? undefined : (e: Event) => { e.stopPropagation(); this.actions.selectArea(this.mode, area) }}>
        <div class="${innerClass} ${canAddRow ? 'has-add-row-affordance' : ''}" style="${innerStyle}">
          ${rows.map((row, index) => this.renderDynamicRow(row, index, area, settings?.columnGap))}
          ${canAddRow ? html`
            <button
              type="button"
              class="add-row-affordance"
              aria-label="Add row"
              @click=${(e: Event) => { e.stopPropagation(); this.actions.addRow(this.mode, area) }}>
              <cse-icon icon-name="plus" icon-style="light"></cse-icon>
            </button>
          ` : nothing}
        </div>
      </div>
    `
  }

  override render() {
    const isListImage = layoutKindFor(this.mode) === 'list'
    // See paddingStyleFor's own comment above for why this stays literal padding-* on
    // .canvas-image-section, unlike production's equivalent.
    const imagePadding = paddingStyleFor(displayConfigForMode(this.imageConfig, this.mode))
    // Same as production's stacksAreasAtTop (base-product-tile.ts).
    const stacksAreasAtTop = this.cardVariant === 'inline' && this.areaPaddingByMode[this.mode].orderActions?.verticalAlign === 'flex-start'

    return html`
      <cse-card variant="${this.cardVariant}" style="${stacksAreasAtTop ? '--cse-card-body-flex-grow:0' : ''}">
        <div
          slot="header"
          class="canvas-image-section ${this.imageFocused === this.mode ? 'image-selected' : ''}"
          style="${imagePadding}"
          @click=${(e: Event) => {
            // Always stopped/prevented, even in mobile mode — csc-product-image renders a real
            // <a> (via its own url property) that would otherwise navigate to the product page
            // on click regardless of whether selection itself is enabled right now.
            e.stopPropagation()
            e.preventDefault()
            if (!this.mobileMode) this.actions.selectImage(this.mode)
          }}>
          ${isListImage ? this.renderListImageZones('above') : nothing}
          <div class="canvas-image-wrapper">
            <csc-product-image
              source="${this.product.picture.small}"
              fallback-source="${this.product.defaultPicture.small}"
              alt="${this.product.name}"
              url="${this.product.pageLink}">
            </csc-product-image>
            ${this.renderImageOverlayGrid()}
          </div>
          ${isListImage ? this.renderListImageZones('below') : nothing}
        </div>

        ${this.renderDynamicArea('productInfo', 'body', 'canvas-info-zones', 'canvas-body-area')}
        ${this.renderDynamicArea('orderActions', 'footer', 'canvas-footer-zones', 'canvas-footer-area')}
      </cse-card>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-tile-configurator-canvas': CSCTileConfiguratorCanvas
  }
}
